/**
 * `/integration/data` — the data browser.
 *
 * The port of the demo's `v-data` view (demo line 1451): a filterable file
 * tree on the left, a document viewer on the right, and every file rendered in
 * the format its own system produces — a CSV as a grid, a spreadsheet as its
 * worksheets, a PDF as its page-one thumbnail and text layer, an email with
 * its headers, a CAN log as raw lines.
 *
 * ## Deep links
 *
 * Four other surfaces link here rather than opening a document themselves, so
 * that the port has exactly one place where a file opens:
 *
 * | query | sender |
 * |---|---|
 * | `?doc=eml:11` | `seed/alerts.ts` — an alert's email-thread evidence |
 * | `?doc=plan:drillplan` | `seed/alerts.ts` — the drill-plan evidence |
 * | `?doc=doc:IR-2026-014`, `?doc=mtg:…`, `?doc=x:XR-…` | `seed/incidents.ts` |
 * | `?doc=<bare file name>` | `shared/assistant/sources.ts` — a source chip |
 * | `?ref=<key>` | `modules/asset360/Asset360Scene.tsx` |
 * | `?conn=<connector>` | `/connectors` — scope the tree to one system |
 *
 * All of them go through `resolveDocKey`, which normalises the six spellings
 * into one key. A reference that matches nothing shows the demo's "no preview
 * built" message naming the key, rather than a blank pane.
 *
 * ## Data
 *
 * The tree needs the lake catalogue (131 KB) plus `comms` and `doc-index`. A
 * document fetches whatever it alone needs when it is opened — see
 * `doc-view.ts`. Opening the browser therefore costs about half a megabyte,
 * not the 5.9 MB the whole pack weighs.
 *
 * @see src/versions/v1/modules/integration/DataBrowserScene.transform.ts
 * @see src/versions/v1/modules/integration/doc-view.ts
 * @see docs/logic/integration/data.md
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Table2 } from 'lucide-react';
import { useDrawer } from '@/app/providers';
import { HelpAnchor, InlineError, SurfacePageBody, SurfacePageHeader, SurfacePageLayout } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '../../chrome-oem/AskStrip';
import { CONNECTORS } from '../../shared/seed/connectors';
import { loadOemDomain, useOemData } from '../../shared/useOemData';
import {
  buildTree,
  connectorHasFolder,
  DEFAULT_DOC_KEY,
  filterByConnector,
  filterTree,
  resolveDocKey,
  treeFileCount,
  type TreeCtx,
} from './DataBrowserScene.transform';
import { fullTableDataset, loadDocView, loadFullTable, type DocView } from './doc-view';
import { DocTree } from './DocTree';
import { DocViewer } from './DocViewer';
import { useLakeIndex } from './useLakeIndex';
// Registering the palette's documents source is a side effect of this import.
import './documents-palette';

/** Demo line 1451 — the view standfirst, verbatim. */
const STANDFIRST =
  'Everything the workspace and the agents read from, organised by the system it came through, ' +
  'in the format that system produces — CSV, XLSX, XML, JSON, JSON-lines, CAN logs, PDF, DOCX, ' +
  'PPTX, EML, TXT, Markdown, VTT. One store: tables, documents, email, transcripts, machine ' +
  'logs and the per-machine records.';

export function DataBrowserScene() {
  const [search, setSearch] = useSearchParams();
  const requested = search.get('doc') ?? search.get('ref');
  const connector = search.get('conn');

  const lake = useLakeIndex();
  const comms = useOemData('comms');
  const docIndex = useOemData('doc-index');
  const [query, setQuery] = useState('');

  /**
   * The one tree label that quotes a lazy dataset —
   * `work_orders.csv — 3,920 orders`.
   *
   * The tree does not fetch 792 KB of work orders to print a number. It is
   * filled in when a work-order-backed document is opened, which `doc-view.ts`
   * fetches anyway and which includes the document the browser opens on. Until
   * then `resolveLabel` truncates the label rather than printing a gap.
   */
  const [workOrderCount, setWorkOrderCount] = useState<number | null>(null);

  const ctx = useMemo<TreeCtx | null>(() => {
    if (!lake.index || !comms.data || !docIndex.data) return null;
    return {
      lake: lake.index,
      comms: comms.data,
      docIndex: docIndex.data,
      workOrderCount,
    };
  }, [comms.data, docIndex.data, lake.index, workOrderCount]);

  const tree = useMemo(() => (ctx ? buildTree(ctx) : []), [ctx]);
  const scoped = useMemo(() => filterByConnector(tree, connector), [connector, tree]);
  const visible = useMemo(() => filterTree(scoped, query), [query, scoped]);

  /** The key the viewer is showing, and whether a reference failed to resolve. */
  const [docKey, setDocKey] = useState<string | null>(null);
  const [missingKey, setMissingKey] = useState<string | null>(null);

  // Resolve whatever arrived in the URL, once the tree's data is in.
  useEffect(() => {
    if (!ctx) return;
    if (!requested) {
      setDocKey(DEFAULT_DOC_KEY);
      setMissingKey(null);
      return;
    }
    const resolved = resolveDocKey(requested, ctx);
    if (resolved) {
      setDocKey(resolved);
      setMissingKey(null);
    } else {
      setDocKey(null);
      setMissingKey(requested);
    }
  }, [ctx, requested]);

  const [view, setView] = useState<DocView | null>(null);
  const [docLoading, setDocLoading] = useState(false);
  const [docError, setDocError] = useState<Error | null>(null);

  useEffect(() => {
    if (!ctx || !docKey) {
      setView(null);
      return;
    }
    let live = true;
    setDocLoading(true);
    setDocError(null);
    loadDocView(docKey, ctx).then(
      (resolved) => {
        if (!live) return;
        setDocLoading(false);
        if (resolved) {
          setView(resolved);
          setMissingKey(null);
        } else {
          setView(null);
          setMissingKey(docKey);
        }
      },
      (cause: unknown) => {
        if (!live) return;
        setDocLoading(false);
        setDocError(cause instanceof Error ? cause : new Error(String(cause)));
      },
    );
    return () => {
      live = false;
    };
  }, [ctx, docKey]);

  // A work-order-backed document has already pulled `work-orders.json`, so
  // reading its row count for the tree label is a cache hit, not a fetch.
  useEffect(() => {
    if (workOrderCount !== null || !docKey) return;
    if (!/^(tab:wos|wo:|sap:)/.test(docKey)) return;
    let live = true;
    loadOemDomain('work-orders').then(
      (payload) => {
        if (live) setWorkOrderCount(payload.wos.rows.length);
      },
      () => {
        /* The document itself reports the failure; the label stays truncated. */
      },
    );
    return () => {
      live = false;
    };
  }, [docKey, workOrderCount]);

  /** Selecting a file writes the key to the URL, so a document is linkable. */
  const select = useCallback(
    (key: string) => {
      const next = new URLSearchParams(search);
      next.set('doc', key);
      next.delete('ref');
      setSearch(next, { replace: true });
    },
    [search, setSearch],
  );

  const clearConnector = useCallback(() => {
    const next = new URLSearchParams(search);
    next.delete('conn');
    setSearch(next, { replace: true });
  }, [search, setSearch]);

  /**
   * Demo line 2424 — "open full table": the whole dataset in the source
   * drawer, where the filter box searches every row and 400 are rendered.
   */
  const { open: openDrawer } = useDrawer();
  const fullTable = docLoading ? null : fullTableDataset(view);
  const openFullTable = useCallback(() => {
    if (!fullTable) return;
    loadFullTable(fullTable).then(
      (payload) => {
        if (!payload) return;
        openDrawer('oem-source', {
          wide: true,
          title: payload.title,
          data: payload as unknown as Record<string, unknown>,
        });
      },
      (cause: unknown) => {
        setDocError(cause instanceof Error ? cause : new Error(String(cause)));
      },
    );
  }, [fullTable, openDrawer]);

  const loadError = lake.error ?? comms.error ?? docIndex.error;
  const treeLoading = !ctx && !loadError;

  return (
    <SurfacePageLayout width="full">
      <Commentable name="DataBrowserScene">
        <SurfacePageBody className="@container max-w-[1500px] space-y-5" as="div">
          <SurfacePageHeader
            kicker="Integration"
            title="Data"
            description={STANDFIRST}
            actions={
              <div className="flex items-center gap-2">
                <span className="mono rounded-full bg-zinc-100 px-2.5 py-1 text-[11px] text-muted dark:bg-zinc-800">
                  {`${treeFileCount(tree).toLocaleString('en-AU')} items`}
                </span>
                <HelpAnchor doc="integration/data" />
              </div>
            }
          />

          <AskStrip view="data" />

          {loadError ? (
            <InlineError>{`The store did not load: ${loadError.message}`}</InlineError>
          ) : null}

          {connector ? (
            <p className="flex flex-wrap items-center gap-2 text-[12px]" style={{ color: 'var(--ink-secondary)' }}>
              <span>
                Showing <b>{connector}</b>
                {connectorHasFolder(connector)
                  ? ''
                  : CONNECTORS.some((c) => c.name === connector)
                    ? ' — it has no folder of its own: its data arrives as a table or a message, in the folder that holds that format'
                    : ' — no system of that name'}
              </span>
              <button
                type="button"
                onClick={clearConnector}
                className="rounded-full border px-2.5 py-0.5 text-[11.5px] transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                style={{ borderColor: 'var(--hairline)', color: 'var(--ink-primary)' }}
              >
                Show every system
              </button>
            </p>
          ) : null}

          <div className="grid min-h-0 grid-cols-1 gap-4 @3xl:grid-cols-[minmax(240px,320px)_minmax(0,1fr)]">
            <Commentable name="DocTree">
              <div
                className="min-h-0 rounded-xl border p-2 @3xl:max-h-[calc(100vh-18rem)]"
                style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
              >
                <DocTree
                  folders={visible}
                  totalFiles={treeFileCount(tree)}
                  connectorCount={CONNECTORS.length}
                  query={query}
                  onQueryChange={setQuery}
                  selectedKey={docKey}
                  onSelect={select}
                  loading={treeLoading}
                />
              </div>
            </Commentable>

            <Commentable name="DocViewer">
              <div
                className="scroll-soft min-h-0 overflow-y-auto rounded-xl border @3xl:max-h-[calc(100vh-18rem)]"
                style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
              >
                {fullTable ? (
                  <div className="flex justify-end px-4 pt-3">
                    <button
                      type="button"
                      onClick={openFullTable}
                      className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11.5px] transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                      style={{ borderColor: 'var(--hairline)', color: 'var(--ink-primary)' }}
                    >
                      <Table2 className="h-3.5 w-3.5" style={{ color: 'var(--accent)' }} aria-hidden="true" />
                      Open full table
                    </button>
                  </div>
                ) : null}
                <DocViewer
                  view={view}
                  loading={docLoading || treeLoading}
                  missingKey={missingKey}
                  error={docError}
                />
              </div>
            </Commentable>
          </div>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
